import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, RefreshCw, Cpu, FileText, MapPin, Award, Sparkles,
  Copy, Wand2, ChevronDown, ExternalLink, Trash2, Undo2, CheckCircle2, ShieldAlert, ShieldCheck,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { ScoreGauge, scoreColor } from "@/components/ScoreGauge";
import { WordPressDialog } from "@/components/WordPressDialog";
import { ReportDialog } from "@/components/ReportDialog";
import { FtpDialog } from "@/components/FtpDialog";
import { SocialDialog } from "@/components/SocialDialog";
import { ReputationTab } from "@/components/ReputationTab";
import { ProofTab } from "@/components/ProofTab";
import { VideoTab } from "@/components/VideoTab";
import { RevenueTab } from "@/components/RevenueTab";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const PILLAR_ICONS = { Technical: Cpu, Content: FileText, Local: MapPin, Authority: Award, "AI Readiness": Sparkles };
const SEV_STYLE = {
  critical: "bg-destructive/15 text-destructive border-destructive/30",
  high: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  medium: "bg-primary/10 text-primary border-primary/30",
  low: "bg-muted text-muted-foreground border-border",
};
const FIX_STYLE = {
  auto: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  assisted: "bg-primary/10 text-primary border-primary/30",
  task: "bg-muted text-muted-foreground border-border",
};
const FIX_LABEL = { auto: "Auto-fix", assisted: "Assisted", task: "Task" };

const PLATFORM_OPTIONS = ["WordPress", "Shopify", "Wix", "Squarespace", "Webflow", "Drupal", "LocalPro OS", "Custom / Static HTML"];

function PillarBar({ pillar, onSelect }) {
  const Icon = PILLAR_ICONS[pillar.name] || Cpu;
  const color = scoreColor(pillar.score);
  return (
    <button type="button" onClick={() => onSelect(pillar.name)}
      className="group w-full border-b border-border py-4 text-left transition-colors last:border-b-0 hover:bg-muted/40"
      data-testid={`pillar-${pillar.name.replace(/\s/g, "-").toLowerCase()}`}
      title={`View the ${pillar.name} issues`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4 text-muted-foreground" />{pillar.name}</div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{pillar.issue_count} issues</span>
          <span className="font-mono text-sm font-medium" style={{ color }}>{pillar.score}</span>
        </div>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <motion.div className="h-full rounded-full" style={{ background: color }} initial={{ width: 0 }} animate={{ width: `${pillar.score}%` }} transition={{ duration: 0.8 }} />
      </div>
    </button>
  );
}

function IssueRow({ issue, auditId, siteId }) {
  const [open, setOpen] = useState(false);
  const [artifact, setArtifact] = useState(issue.fix_artifact || null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [fixStatus, setFixStatus] = useState(issue.fix_status || "open");
  const [gate, setGate] = useState(issue.fix_gate || null);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/audits/${auditId}/issues/${issue.id}/generate-fix`);
      setArtifact(data.artifact);
      setFixStatus("fix_generated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate fix");
    } finally {
      setLoading(false);
    }
  };

  const apply = async (approve_gate = false) => {
    setApplying(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/apply-fix/${issue.id}`, { artifact, approve_gate });
      setFixStatus(data.fix_status);
      setGate(data.gate || null);
      if (data.held) toast.warning("Held for your approval", { description: data.gate.reasons.join(" · ") });
      else if (data.delivered) toast.success(`Fix applied live (${data.delivery})`);
      else if (data.applicable === false) toast.message("Not auto-applicable", { description: data.note });
      else toast.success("Fix file ready — deploy via your pipeline / FTP");
      if (data.content) setArtifact(data.content);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const rollback = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/rollback-fix/${issue.id}`, {});
      setFixStatus(data.fix_status);
      if (data.rolled_back) toast.success(`Fix rolled back (${data.delivery})`);
      else toast.message("Marked as not applied", { description: data.note });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Rollback failed");
    } finally {
      setApplying(false);
    }
  };

  const copy = () => { navigator.clipboard.writeText(artifact || ""); toast.success("Copied to clipboard"); };

  return (
    <div className="border-b border-border last:border-b-0" data-testid="audit-issue-row">
      <button onClick={() => setOpen(!open)} className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/50" data-testid="issue-toggle">
        <ChevronDown className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">{issue.title}</p>
            {fixStatus === "fix_applied" && <CheckCircle2 className="h-4 w-4 text-emerald-500" data-testid="fix-applied-icon" />}
          </div>
          <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{issue.detail}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <Badge variant="outline" className={SEV_STYLE[issue.severity]}>{issue.severity}</Badge>
          <Badge variant="outline" className={FIX_STYLE[issue.fix_type]}>{FIX_LABEL[issue.fix_type]}</Badge>
        </div>
      </button>

      {open && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden px-4 pb-4 pl-11">
          <div className="rounded-md border border-border bg-background p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">Why it matters</p>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/90">{issue.why_it_matters}</p>
            <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Recommendation</p>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/90">{issue.recommendation}</p>

            <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span>Effort: <span className="text-foreground">{issue.effort}</span></span><span>·</span>
              <span>Impact: <span className="text-foreground">{issue.impact}</span></span><span>·</span>
              <span>Pillar: <span className="text-foreground">{issue.pillar}</span></span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {!artifact && (
                <Button size="sm" onClick={generate} disabled={loading} data-testid="generate-fix-button">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />} Generate fix
                </Button>
              )}
              {artifact && fixStatus !== "fix_applied" && (
                <Button size="sm" variant="outline" onClick={() => apply(false)} disabled={applying} data-testid="apply-fix-button">
                  {applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Apply fix
                </Button>
              )}
              {fixStatus === "fix_applied" && (
                <Button size="sm" variant="outline" onClick={rollback} disabled={applying} data-testid="rollback-fix-button">
                  {applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />} Undo
                </Button>
              )}
            </div>

            {gate && gate.status !== "approved" && fixStatus !== "fix_applied" && (
              <div className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/5 p-3" data-testid="content-gate-hold">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400">
                  <ShieldAlert className="h-4 w-4" /> Held before publishing
                </p>
                <ul className="mt-2 list-inside list-disc space-y-0.5 text-sm text-foreground/90">
                  {gate.reasons.map((reason, i) => <li key={i} data-testid="content-gate-reason">{reason}</li>)}
                </ul>
                {gate.proposed && <pre className="mt-2 whitespace-pre-wrap rounded border border-border bg-card p-2 font-mono text-xs">{gate.proposed}</pre>}
                <p className="mt-2 text-xs text-muted-foreground">
                  {gate.checked ? "Nothing was published." : "The safety check could not run, so nothing was published."} Review the text above, then publish it yourself if you're happy with it. Copy that can't be confirmed from the site's own data is held too, not just copy that looks wrong.
                </p>
                <Button size="sm" className="mt-2" onClick={() => apply(true)} disabled={applying} data-testid="approve-publish-button">
                  {applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Approve &amp; publish
                </Button>
              </div>
            )}

            {artifact && (
              <div className="mt-4">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                    {fixStatus === "fix_applied" ? "Applied fix" : "AI-generated fix"}
                  </span>
                  <Button size="sm" variant="ghost" onClick={copy} data-testid="copy-fix-button"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy</Button>
                </div>
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-card p-3 font-mono text-xs leading-relaxed">{artifact}</pre>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default function SiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reauditing, setReauditing] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);
  const [ftpConfigured, setFtpConfigured] = useState(false);
  const [filter, setFilter] = useState("All");
  const [tab, setTab] = useState("overview");

  const showPillar = (name) => {
    setFilter(name);
    setTab("issues");
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const savePlatform = async (val) => {
    try {
      await api.patch(`/sites/${id}`, { platform_override: val });
      patchLocal({ platform_override: val || null });
      toast.success(val ? `Platform set to ${val}` : "Back to automatic detection");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save the platform");
    }
  };
  const poll = useRef(null);

  const load = () => api.get(`/sites/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); api.get(`/sites/${id}/ftp-config`).then((r) => setFtpConfigured(!!r.data.configured)).catch(() => {}); }, [id]);

  // coming back from Stripe Checkout: confirm the payment, then show the add-on as active
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("checkout") === "cancelled") {
      toast.message("Checkout cancelled — nothing was charged.");
      window.history.replaceState({}, "", `/site/${id}`);
      return;
    }
    const session = q.get("session_id");
    if (q.get("checkout") !== "success" || !session) return;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/entitlement/checkout/${session}`);
        if (data.payment_status === "paid") {
          toast.success("Payment received — the add-on is live on this site.");
          window.history.replaceState({}, "", `/site/${id}`);
          load();
          return;
        }
      } catch (err) { /* keep polling, Stripe may still be settling */ }
      if (tries < 10) setTimeout(tick, 2000);
      else toast.message("Payment is still settling. Refresh in a moment.");
    };
    tick();
  }, [id]);

  useEffect(() => {
    clearInterval(poll.current);
    if (data && data.site && data.site.status === "auditing") poll.current = setInterval(load, 2500);
    return () => clearInterval(poll.current);
  }, [data]);

  const patchLocal = (patch) => setData((d) => ({ ...d, site: { ...d.site, ...patch } }));

  const reaudit = async () => {
    setReauditing(true);
    try { await api.post(`/sites/${id}/reaudit`); await load(); toast.success("Re-audit complete"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Re-audit failed"); }
    finally { setReauditing(false); }
  };

  const remove = async () => { await api.delete(`/sites/${id}`); toast.success("Site removed"); navigate("/"); };

  const applyAll = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/sites/${id}/apply-all-fixes`, {});
      toast.success(`Applied ${data.applied} of ${data.candidates} safe fixes live`,
        data.held ? { description: `${data.held} held for your approval — open those issues to review and publish` } : undefined);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Bulk apply failed"); }
    finally { setBulkBusy(false); }
  };

  const rollbackAll = async () => {
    const applied = (data?.audit?.issues || []).filter((i) => i.fix_status === "fix_applied").length;
    if (!window.confirm(`This will undo all ${applied} applied fixes and re-audit the site. Continue?`)) return;
    setUndoBusy(true);
    try {
      const { data: res } = await api.post(`/sites/${id}/rollback-all-fixes`, {});
      toast.success(`Rolled back ${res.rolled_back} of ${res.candidates} fixes${res.new_score != null ? ` · new score ${res.new_score}` : ""}`);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Bulk undo failed"); }
    finally { setUndoBusy(false); }
  };

  if (data === null) return <AppLayout><div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading site…</div></AppLayout>;
  if (data === false) return <AppLayout><p className="text-muted-foreground">Site not found.</p></AppLayout>;

  const { site, audit } = data;
  let host = site.url;
  try { host = new URL(site.url).hostname.replace("www.", ""); } catch {}

  if (site.status === "auditing" || !audit) {
    return (
      <AppLayout>
        <button onClick={() => navigate("/")} className="mb-5 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Portfolio</button>
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-24 text-center" data-testid="site-auditing">
          {site.status === "error" ? (
            <><p className="font-display text-xl font-semibold text-destructive">Audit failed</p><p className="mt-2 text-sm text-muted-foreground">{site.error || "The site could not be reached."}</p><Button className="mt-6" onClick={reaudit} disabled={reauditing}>{reauditing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Retry</Button></>
          ) : (
            <><Loader2 className="mb-4 h-8 w-8 animate-spin text-primary" /><p className="font-display text-xl font-semibold">Auditing {host}…</p><p className="mt-2 text-sm text-muted-foreground">Crawling, scoring and generating your fix list.</p></>
          )}
        </div>
      </AppLayout>
    );
  }

  const meta = audit.meta || {};
  const pillars = ["All", ...audit.pillars.map((p) => p.name)];
  const issues = filter === "All" ? audit.issues : audit.issues.filter((i) => i.pillar === filter);
  const effectivePlatform = site.platform_override || meta.platform;
  const noDirectWrite = effectivePlatform === "LocalPro OS";
  const metaItems = [
    ["Platform", effectivePlatform], ["Status", meta.status_code], ["Words", meta.word_count],
    ["PageSpeed", meta.pagespeed_score != null ? `${meta.pagespeed_score}/100` : "n/a"],
    ["Schema", meta.schema_types?.length ? meta.schema_types.join(", ") : "none"],
    ["llms.txt", meta.has_llms_txt ? "yes" : "no"],
  ];

  return (
    <AppLayout>
      <button onClick={() => navigate("/")} className="mb-5 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-button"><ArrowLeft className="h-4 w-4" /> Portfolio</button>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-3xl font-semibold tracking-tight">{site.business_name || host}</h1>
            <a href={site.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-4 w-4" /></a>
          </div>
          <p className="mt-1 font-mono text-sm text-muted-foreground">{site.url}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <WordPressDialog siteId={site.id} siteUrl={site.url} connected={site.connection_type === "wordpress"} onChange={(c) => patchLocal({ connection_type: c })} />
          <FtpDialog siteId={site.id} configured={ftpConfigured} onChange={setFtpConfigured} />
          <SocialDialog siteId={site.id} />
          <ReportDialog site={site} onSaved={(patch) => patchLocal(patch)} />
          <Button variant="outline" onClick={reaudit} disabled={reauditing} data-testid="reaudit-button">
            {reauditing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Re-audit
          </Button>
          <Button variant="ghost" size="icon" onClick={remove} data-testid="delete-site-button"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="site-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">Issues & Fixes ({audit.issues.length})</TabsTrigger>
          <TabsTrigger value="reputation" data-testid="tab-reputation">Reputation</TabsTrigger>
          <TabsTrigger value="proof" data-testid="tab-proof">Pop-ups</TabsTrigger>
          <TabsTrigger value="video" data-testid="tab-video">Videos</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-6">
              <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Visibility Score</p>
              <ScoreGauge score={audit.score} />
            </div>
            <div className="lg:col-span-2 rounded-lg border border-border bg-card p-6">
              <div className="mb-4 flex items-center gap-2 text-emerald-600 dark:text-emerald-400"><Sparkles className="h-4 w-4" /><p className="text-xs font-semibold uppercase tracking-widest">AI Executive Summary</p></div>
              <p className="text-sm leading-relaxed" data-testid="audit-summary">{audit.summary}</p>
              <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border pt-5 sm:grid-cols-3">
                {metaItems.map(([k, v]) => (<div key={k}><p className="text-xs text-muted-foreground">{k}</p><p className="mt-0.5 truncate font-mono text-sm">{String(v)}</p></div>))}
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-5">
                <span className="text-xs text-muted-foreground">Platform override</span>
                <select value={site.platform_override || ""} onChange={(e) => savePlatform(e.target.value)}
                  data-testid="platform-override" className="h-8 rounded-md border border-input bg-background px-2 text-xs">
                  <option value="">{`Auto-detect (${meta.platform_detected || meta.platform || "unknown"})`}</option>
                  {PLATFORM_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                {noDirectWrite && (
                  <span className="text-xs text-amber-600 dark:text-amber-400" data-testid="no-direct-write-note">
                    Files here are served from the CDN, not the FTP docroot — direct fixes are disabled.
                  </span>
                )}
              </div>
            </div>
            <div className="lg:col-span-3 rounded-lg border border-border bg-card p-6">
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Pillar Breakdown</p>
              {audit.pillars.map((p) => <PillarBar key={p.name} pillar={p} onSelect={showPillar} />)}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="issues" className="mt-6">
          {noDirectWrite ? (
            <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3" data-testid="no-direct-write-banner">
              <p className="text-sm text-foreground/90">
                This site runs on <span className="font-medium">{effectivePlatform}</span> and is served from a CDN pull zone,
                not from the FTP or WordPress docroot. A write would succeed and never go live, so direct fixes are off.
                Generate each fix here and paste it into your site builder.
              </p>
            </div>
          ) : (site.connection_type === "wordpress" || ftpConfigured) && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
              <p className="text-sm text-muted-foreground">
                Connected via {site.connection_type === "wordpress" ? "WordPress" : "FTP"}. Apply every safe auto-fix live, or roll them all back.
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={applyAll} disabled={bulkBusy || undoBusy} data-testid="apply-all-fixes-button">
                  {bulkBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />} Apply all safe fixes
                </Button>
                <Button size="sm" variant="outline" onClick={rollbackAll} disabled={bulkBusy || undoBusy} data-testid="rollback-all-fixes-button">
                  {undoBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />} Roll back all
                </Button>
              </div>
            </div>
          )}
          <div className="mb-4 flex flex-wrap gap-2" data-testid="pillar-filters">
            {pillars.map((p) => (
              <button key={p} onClick={() => setFilter(p)} data-testid={`filter-${p.replace(/\s/g, "-").toLowerCase()}`}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === p ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{p}</button>
            ))}
          </div>
          <div className="rounded-lg border border-border bg-card">
            {issues.length === 0 ? (<p className="p-6 text-sm text-muted-foreground">No issues in this pillar. Nicely done.</p>)
              : (issues.map((i) => <IssueRow key={i.id} issue={i} auditId={audit.id} siteId={site.id} />))}
          </div>
        </TabsContent>

        <TabsContent value="reputation" className="mt-6">
          <ReputationTab site={site} />
        </TabsContent>

        <TabsContent value="proof" className="mt-6">
          <ProofTab site={site} />
        </TabsContent>

        <TabsContent value="video" className="mt-6">
          <VideoTab site={site} />
        </TabsContent>

        <TabsContent value="revenue" className="mt-6">
          <RevenueTab site={site} />
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
